import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import { canSeeInternalNotes } from '../../../utils/rbac';
import PermissionGate from '../../../components/PermissionGate';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const NotesTab = ({ userRole, claimId, notes, setNotes, refreshClaim, S }) => {
  const { t } = useTranslation();
  const [noteText, setNoteText] = useState('');
  const [isInternal, setIsInternal] = useState(true);
  const [saving, setSaving] = useState(false);
  const showInternal = canSeeInternalNotes(userRole);
  // Notes tab shows only internal notes; external notes live in the Communications tab
  const visibleNotes = notes.filter(n => n.internal !== false);

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    if (!claimId) {
      toast.error(t('screens.claimsScreen.detail.claimNotLoaded'));
      return;
    }
    setSaving(true);
    try {
      const resp = await axios.post(`${BASE_URL}/claims/${claimId}/notes`, { text: noteText.trim(), internal: isInternal }, { withCredentials: true });
      setNotes(resp.data.data || []);
      setNoteText('');
      toast.success(t('screens.claimsScreen.detail.noteAdded'));
      // Rebuild the timeline (which is populated inside useClaimDetailFunctions
      // from statusHistory + notes) so the new note appears on the Timeline
      // tab without needing a page refresh. Per T-10.6.
      if (typeof refreshClaim === 'function') refreshClaim();
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.msg || t('screens.claimsScreen.detail.saveNoteFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PermissionGate resource='claims' action='note'>
        <div style={{ background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 14, padding: 20, marginBottom: 24 }}>
          <textarea
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            placeholder={t('screens.claimsScreen.detail.addNotePlaceholder')}
            style={{
              width: '100%',
              minHeight: 80,
              padding: 14,
              background: '#fff',
              border: `1px solid ${S.borderLight}`,
              borderRadius: 10,
              fontSize: 13,
              color: S.textPrimary,
              resize: 'vertical',
              outline: 'none',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {showInternal && (
                <button
                  onClick={() => setIsInternal(true)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    border: isInternal ? `1px solid ${S.burgundy}` : `1px solid ${S.borderLight}`,
                    background: isInternal ? 'rgba(128,0,32,0.07)' : '#fff',
                    color: isInternal ? S.burgundy : S.textSecondary,
                  }}
                >
                  {t('screens.claimsScreen.detail.internal')}
                </button>
              )}
              <button
                onClick={() => setIsInternal(false)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: !isInternal ? '1px solid #3b82f6' : `1px solid ${S.borderLight}`,
                  background: !isInternal ? '#eff6ff' : '#fff',
                  color: !isInternal ? '#2563eb' : S.textSecondary,
                }}
              >
                {t('screens.claimsScreen.detail.external')}
              </button>
            </div>
            <button
              onClick={handleAddNote}
              disabled={!noteText.trim() || saving}
              style={{
                padding: '8px 20px',
                background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: noteText.trim() && !saving ? 'pointer' : 'not-allowed',
                opacity: noteText.trim() && !saving ? 1 : 0.5,
              }}
            >
              {saving ? t('screens.claimsScreen.detail.saving') : t('screens.claimsScreen.detail.addNote')}
            </button>
          </div>
        </div>
      </PermissionGate>
      {visibleNotes.length === 0 && <div style={{ padding: '40px 0', textAlign: 'center', color: S.textMuted, fontSize: 13 }}>{t('screens.claimsScreen.detail.noNotes')}</div>}
      {visibleNotes.map((note, i) => {
        const initials = (note.authorName || note.user || '?')
          .split(' ')
          .map(p => p[0])
          .join('')
          .slice(0, 2)
          .toUpperCase();
        const isInt = note.internal !== false;
        const timeStr = note.createdAt ? new Date(note.createdAt).toLocaleString() : note.time || '';
        return (
          <div
            key={note._id || note.id || i}
            style={{
              marginBottom: 16,
              padding: '18px 20px',
              background: isInt ? 'rgba(128,0,32,0.02)' : S.bgCard,
              border: `1px solid ${isInt ? 'rgba(128,0,32,0.1)' : S.borderLight}`,
              borderRadius: 12,
              borderLeft: `3px solid ${isInt ? S.burgundy : '#3b82f6'}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {initials}
                </div>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary }}>{note.authorName || note.user || '—'}</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {showInternal && (
                  <span
                    style={{
                      padding: '3px 8px',
                      borderRadius: 4,
                      fontSize: 10,
                      fontWeight: 700,
                      background: isInt ? 'rgba(128,0,32,0.08)' : '#eff6ff',
                      color: isInt ? S.burgundy : '#2563eb',
                    }}
                  >
                    {isInt ? t('screens.claimsScreen.detail.badgeInternal') : t('screens.claimsScreen.detail.badgeExternal')}
                  </span>
                )}
                <span style={{ fontSize: 12, color: S.textMuted }}>{timeStr}</span>
              </div>
            </div>
            <div style={{ fontSize: 13, color: S.textSecondary, lineHeight: 1.6 }}>{note.text}</div>
          </div>
        );
      })}
    </div>
  );
};

export default NotesTab;
